import type { PackageGroupPolicySnapshot } from '../../../package-integration/packageGroups';
import { catalogDeviceStock } from '../mock/pricing';
import { defaultQuoteDiscountForBusinessMode } from './businessModes';
import {
  deviceFieldSnapshotForQuoteItem,
  requireDeviceSku,
} from './deviceCatalog';
import { resizeQuoteExclusiveLine } from './quoteLocalPricing';
import {
  assertDiscountPercent,
  assertQuoteQuantity,
  createPricingBaseSnapshot,
  occurrenceSourceLineCents,
  planProductPrice,
  planProductSubtotal,
} from './quoteTotals';
import type {
  BusinessModeCode,
  QuoteItem,
  QuoteSelectionDevice,
  QuoteSourceSnapshot,
} from './types';

export interface QuoteItemSelection {
  device: QuoteSelectionDevice;
  source: QuoteSourceSnapshot;
  quoteGroupId?: string;
  containerInstanceId?: string;
  /** `quote-local-package` uses equipment package-item price; display/plan groups retain relation pricing. */
  quoteGroupKind?: 'quote-local-package' | 'display';
  sourceRevisionId?: string;
  /** Trusted catalog/local-definition projection used only for immediate UI pricing. */
  packageGroupPolicySnapshot?: PackageGroupPolicySnapshot | null;
  quantity?: number;
  relation?: {
    relationId: string;
    packageId: string;
    replacementPoolId?: string | null;
    packageItemPrice: number | null;
    packageItemMonthlySubtotal?: number;
    relationQuantity: number;
    planId?: string;
    planDiscount?: number;
    planProductPrice?: number;
    planProductSubtotal?: number;
    planSubtotal?: number;
    packageMonthlySubtotal?: number;
    packageDiscountedQuote?: number;
  };
}

export interface QuoteItemSourceGroup extends QuoteSourceSnapshot {
  sourceKey: string;
  itemCount: number;
  items: QuoteItem[];
}

export function quoteSourceKey(source: QuoteSourceSnapshot): string {
  return `${source.sourceType}:${source.sourceId}`;
}

export function quoteLineKey(
  source: QuoteSourceSnapshot,
  sku: string | null,
  packageRelationId?: string,
  quoteGroupId?: string,
): string {
  if (source.sourceFragment)
    return `${quoteSourceKey(source)}:instance:${source.sourceFragment.instanceKey}:fragment:${source.sourceFragment.fragmentKey}`;
  if (source.formalPlanOccurrence)
    return `${quoteSourceKey(source)}:${source.formalPlanOccurrence.occurrenceKey}:${source.formalPlanOccurrence.itemKey}`;
  return quoteGroupId
    ? `${quoteSourceKey(source)}:${sku}:quote-group:${quoteGroupId}`
    : packageRelationId
      ? `${quoteSourceKey(source)}:${source.packageRevisionId ?? ''}:${source.planPackageMembershipId ?? ''}:${sku}:relation:${packageRelationId}`
      : `${quoteSourceKey(source)}:${sku}`;
}

export function groupQuoteItemsBySource(
  items: QuoteItem[],
): QuoteItemSourceGroup[] {
  const grouped = new Map<string, QuoteItemSourceGroup>();
  for (const item of items) {
    const sourceKey = quoteSourceKey(item);
    const group = grouped.get(sourceKey);
    if (group) {
      group.items.push(item);
      group.itemCount += item.quantity;
      continue;
    }
    grouped.set(sourceKey, {
      sourceKey,
      sourceType: item.sourceType,
      sourceId: item.sourceId,
      sourceName: item.sourceName,
      sourceCode: item.sourceCode,
      itemCount: item.quantity,
      items: [item],
    });
  }
  return [...grouped.values()];
}

// A package's exact source revision is also its package revision. Readback
// materializes both fields, so normalize catalog selections before keying them.
function selectionSourceSnapshot(
  selection: QuoteItemSelection,
): QuoteSourceSnapshot {
  return selection.source.sourceType === 'package' &&
    !selection.source.packageRevisionId &&
    selection.sourceRevisionId
    ? { ...selection.source, packageRevisionId: selection.sourceRevisionId }
    : selection.source;
}

export function mergeQuoteItemSelections(
  currentItems: QuoteItem[],
  selections: QuoteItemSelection[],
  initialSequence: number,
  businessModeCode: BusinessModeCode = 'rental',
): { items: QuoteItem[]; nextSequence: number } {
  const items = currentItems.map((item) => ({ ...item }));
  const byLineKey = new Map(
    items
      .filter((item) => !item.sourceFragment)
      .map((item) => [
        `${quoteLineKey({ ...item, packageRevisionId: item.packageRevisionId ?? (item.sourceType === 'package' ? item.sourceRevisionId : undefined) }, item.sku, item.packageRelationId, item.quoteGroupId)}:${item.sourceRevisionId ?? ''}:container:${item.containerInstanceId ?? ''}`,
        item,
      ]),
  );
  const sourceSnapshots = new Map<string, QuoteSourceSnapshot>(
    items
      .filter((item) => !item.sourceFragment)
      .map((item) => [
        `${quoteSourceKey(item)}:${item.sourceRevisionId ?? ''}:${item.packageRevisionId ?? (item.sourceType === 'package' ? item.sourceRevisionId : undefined) ?? ''}:${item.planPackageMembershipId ?? ''}`,
        {
          sourceType: item.sourceType,
          sourceId: item.sourceId,
          sourceName: item.sourceName,
          sourceCode: item.sourceCode,
          ...((item.packageRevisionId ??
          (item.sourceType === 'package' ? item.sourceRevisionId : undefined))
            ? {
                packageRevisionId:
                  item.packageRevisionId ?? item.sourceRevisionId,
              }
            : {}),
          ...(item.planPackageMembershipId
            ? { planPackageMembershipId: item.planPackageMembershipId }
            : {}),
        } satisfies QuoteSourceSnapshot,
      ]),
  );
  let nextSequence = initialSequence;
  const usedItemIds = new Set(items.map((item) => item.itemId));

  for (const selection of selections) {
    const sku = requireDeviceSku(selection.device);
    const isQuoteLocalPackage =
      Boolean(selection.quoteGroupId) &&
      selection.quoteGroupKind === 'quote-local-package' &&
      selection.relation === undefined;
    const localPackageUnitPrice = isQuoteLocalPackage
      ? selection.device.packageItemPrice
      : undefined;
    if (
      isQuoteLocalPackage &&
      (localPackageUnitPrice === undefined ||
        localPackageUnitPrice === null ||
        !Number.isFinite(localPackageUnitPrice) ||
        localPackageUnitPrice < 0)
    ) {
      throw new Error('设备套餐单品价未维护，暂不能加入报价内套餐');
    }
    const pricingBaseSnapshot = createPricingBaseSnapshot(businessModeCode, {
      rentalPrice: selection.device.rentalPrice,
      retailPriceTax13: selection.device.retailPriceTax13,
      taxInclusivePlanPrice: selection.device.taxInclusivePlanPrice,
      packageItemPrice:
        selection.relation?.packageItemPrice ?? localPackageUnitPrice,
    });
    const quantity =
      selection.quantity ?? selection.relation?.relationQuantity ?? 1;
    assertQuoteQuantity(quantity);
    if (selection.relation) {
      if (
        selection.relation.packageItemPrice === null ||
        !Number.isFinite(selection.relation.packageItemPrice) ||
        selection.relation.packageItemPrice < 0
      ) {
        throw new Error('套餐单品价必须是大于等于 0 的有限金额');
      }
      assertQuoteQuantity(selection.relation.relationQuantity);
      if (selection.relation.planDiscount !== undefined) {
        assertDiscountPercent(selection.relation.planDiscount, '方案专属折扣');
      }
    }
    const selectedSource = selectionSourceSnapshot(selection);
    const sourceKey = `${quoteSourceKey(selectedSource)}:${selection.sourceRevisionId ?? ''}:${selectedSource.packageRevisionId ?? ''}:${selectedSource.planPackageMembershipId ?? ''}`;
    const source = selectedSource.formalPlanOccurrence
      ? selectedSource
      : (sourceSnapshots.get(sourceKey) ?? selectedSource);
    const key = `${quoteLineKey(
      source,
      selection.device.sku,
      selection.relation?.relationId,
      selection.quoteGroupId,
    )}:${selection.sourceRevisionId ?? ''}:container:${selection.containerInstanceId ?? ''}`;
    const existing = byLineKey.get(key);
    if (existing) {
      const priorQuantity = existing.quantity;
      const nextQuantity = priorQuantity + quantity;
      if (!Number.isSafeInteger(nextQuantity))
        throw new Error('数量超出安全范围');
      const relationQuantity = existing.packageRelationQuantitySnapshot ?? 1;
      const resized = resizeQuoteExclusiveLine(
        { ...existing, quantity: nextQuantity },
        priorQuantity / relationQuantity,
        nextQuantity / relationQuantity,
      );
      existing.quantity = nextQuantity;
      existing.deviceFieldSnapshot = resized.deviceFieldSnapshot;
      if (existing.planProductSubtotalSnapshot !== undefined) {
        const relationQuantity = existing.packageRelationQuantitySnapshot ?? 0;
        const priorPackages = priorQuantity / relationQuantity;
        const nextPackages = existing.quantity / relationQuantity;
        const priorCents = Math.round(
          existing.planProductSubtotalSnapshot * 100,
        );
        if (
          !Number.isSafeInteger(priorPackages) ||
          priorPackages < 1 ||
          !Number.isSafeInteger(nextPackages) ||
          priorCents % priorPackages !== 0
        )
          throw new Error('方案关系金额无法按整套数量合并');
        existing.planProductSubtotalSnapshot =
          ((priorCents / priorPackages) * nextPackages) / 100;
      }
      if (existing.formalPlanOccurrence)
        existing.formalPlanOccurrence = {
          ...existing.formalPlanOccurrence,
          sourceLineCents: occurrenceSourceLineCents(existing),
        };
      continue;
    }
    const planProductPriceSnapshot =
      selection.relation?.planId === undefined
        ? undefined
        : selection.sourceRevisionId &&
            selection.relation.planProductPrice !== undefined
          ? selection.relation.planProductPrice
          : selection.relation.planDiscount !== undefined
            ? ((selection.sourceRevisionId
                ? selection.relation.planProductPrice
                : undefined) ??
              planProductPrice(
                selection.relation.packageItemPrice,
                selection.relation.planDiscount,
              ))
            : undefined;
    const planProductSubtotalSnapshot =
      planProductPriceSnapshot !== undefined && selection.relation
        ? ((selection.source.formalPlanOccurrence || selection.sourceRevisionId
            ? selection.relation.planProductSubtotal
            : undefined) ??
          (selection.source.formalPlanOccurrence
            ? planProductPriceSnapshot * selection.relation.relationQuantity
            : planProductSubtotal(
                selection.relation.packageItemPrice,
                selection.relation.planDiscount ?? 100,
                selection.relation.relationQuantity,
              )))
        : undefined;

    let itemId = `ITEM-${String(nextSequence).padStart(4, '0')}`;
    while (usedItemIds.has(itemId)) {
      nextSequence += 1;
      itemId = `ITEM-${String(nextSequence).padStart(4, '0')}`;
    }
    usedItemIds.add(itemId);
    const item: QuoteItem = {
      itemId,
      deviceId: selection.device.id,
      name: selection.device.name,
      sku,
      ...source,
      ...(selection.sourceRevisionId
        ? { sourceRevisionId: selection.sourceRevisionId }
        : {}),
      quantity,
      unitPrice:
        selection.relation?.packageItemPrice ??
        localPackageUnitPrice ??
        selection.device.rentalPrice ??
        pricingBaseSnapshot.amount,
      discount: defaultQuoteDiscountForBusinessMode(businessModeCode),
      pricingSnapshot: {
        ...(selection.packageGroupPolicySnapshot === undefined
          ? {}
          : { packageGroupPolicy: selection.packageGroupPolicySnapshot }),
      },
      pricingBaseSnapshot,
      // QuoteItem.unitPrice is retained for legacy pricing display only. The
      // device-master "单价" fact is frozen independently below.
      deviceFieldSnapshot: deviceFieldSnapshotForQuoteItem(selection.device),
      ...(selection.quoteGroupId
        ? { quoteGroupId: selection.quoteGroupId }
        : {}),
      ...(selection.containerInstanceId
        ? { containerInstanceId: selection.containerInstanceId }
        : {}),
      ...(localPackageUnitPrice === undefined || localPackageUnitPrice === null
        ? {}
        : { packageItemPriceSnapshot: localPackageUnitPrice }),
      ...(selection.relation
        ? {
            packageRelationId: selection.relation.relationId,
            packageId: selection.relation.packageId,
            ...(selection.relation.replacementPoolId
              ? { replacementPoolId: selection.relation.replacementPoolId }
              : {}),
            packageItemPriceSnapshot: selection.relation
              .packageItemPrice as number,
            ...(selection.relation.packageItemMonthlySubtotal !== undefined
              ? {
                  packageItemMonthlySubtotalSnapshot:
                    selection.relation.packageItemMonthlySubtotal,
                }
              : {}),
            packageRelationQuantitySnapshot:
              selection.relation.relationQuantity,
            ...(selection.relation.planId
              ? { planId: selection.relation.planId }
              : {}),
            ...(selection.relation.planDiscount !== undefined
              ? { planDiscountSnapshot: selection.relation.planDiscount }
              : {}),
            ...(planProductPriceSnapshot !== undefined
              ? {
                  planProductPriceSnapshot,
                }
              : {}),
            ...(planProductSubtotalSnapshot !== undefined
              ? {
                  planProductSubtotalSnapshot,
                }
              : {}),
            ...(selection.relation.planSubtotal !== undefined
              ? {
                  planSubtotalSnapshot: selection.relation.planSubtotal,
                }
              : {}),
            packageMonthlySubtotalSnapshot:
              selection.relation.packageMonthlySubtotal,
            packageDiscountedQuoteSnapshot:
              selection.relation.packageDiscountedQuote,
          }
        : {}),
      stock: catalogDeviceStock(selection.device),
    };
    nextSequence += 1;
    items.push(item);
    byLineKey.set(key, item);
    sourceSnapshots.set(sourceKey, source);
  }

  return { items, nextSequence };
}
